// Builds one TrendBucketTotals per chart bucket from rollup rows plus the
// ragged-edge raw rows — the trend equivalent of rollupMetrics.ts.
//
// Bucketing must agree with getBucketKey() exactly, or a point lands in the wrong
// column and the chart is quietly wrong. Two rules, and they differ:
//
//   week              bucket = the week's Monday, which IS week_start
//   month, trailing   bucket = the month holding the week's THURSDAY
//   month, calendar   bucket = each DATE's own month, so a straddling week
//                     belongs to two buckets and cannot come from one rollup row
//
// splitRange() already withholds straddling weeks for the last case, so anything
// reaching here as a whole week fits exactly one bucket.

import type { TrendBucketTotals } from "./trendMetrics";
import type { DvmWeekRow, RollupSiteWeekRow } from "./rollupMetrics";
import type { SchedulingRow } from "../types/scheduling";
import type { Granularity, ScopeFilters } from "../types/filters";
import type { DateRange, PeriodFamily } from "./periods";
import { getBucketKey, isBucketIncluded } from "./periods";
import { sumSelectedKindMinutes } from "./metrics";

const num = (v: unknown): number => (v == null ? 0 : Number(v));

function popcount7(mask: number): number {
  let n = 0;
  for (let b = 0; b < 7; b++) if (mask & (1 << b)) n++;
  return n;
}

function matchesScope(
  row: {
    country_code?: string | null;
    division_name?: string | null;
    group_name?: string | null;
    location_id?: string | null;
    resource_id?: string | null;
  },
  scope: ScopeFilters
): boolean {
  if (scope.countryCode && row.country_code !== scope.countryCode) return false;
  if (scope.divisionName && row.division_name !== scope.divisionName) return false;
  if (scope.groupName && row.group_name !== scope.groupName) return false;
  if (scope.locationId && row.location_id !== scope.locationId) return false;
  if (scope.resourceId && row.resource_id !== scope.resourceId) return false;
  return true;
}

// A rollup row's week_start is a Monday, so bucketing it is bucketing that Monday.
// Passing the Monday through getBucketKey rather than reimplementing the rule
// keeps the two in step -- including the Thursday offset for trailing months.
function bucketOfWeek(weekStart: string, granularity: Granularity, family: PeriodFamily): string {
  return getBucketKey(weekStart, granularity, family);
}

interface Acc {
  sched: number;
  worked: number;
  blockoff: number;
  allSeen: number;
  avail: number;
  selectedKind: number;
  medRev: number;
  completed: number;
  // Masks OR-ed per (doctor, week) and per week, so a doctor at two sites on the
  // same day is counted once. Resolved to counts only at the end.
  perDvmWeek: Map<string, number>;
  perWeek: Map<string, number>;
  // Edge days are disjoint from whole weeks, so these add rather than merge.
  edgeDvmDays: Set<string>;
  edgeDates: Set<string>;
  edgeDvmWeeks: Set<string>;
}

const emptyAcc = (): Acc => ({
  sched: 0,
  worked: 0,
  blockoff: 0,
  allSeen: 0,
  avail: 0,
  selectedKind: 0,
  medRev: 0,
  completed: 0,
  perDvmWeek: new Map(),
  perWeek: new Map(),
  edgeDvmDays: new Set(),
  edgeDates: new Set(),
  edgeDvmWeeks: new Set(),
});

export interface RollupTrendInput {
  siteWeeks: RollupSiteWeekRow[];
  dvmWeeks: DvmWeekRow[];
  edgeRows: SchedulingRow[];
  range: DateRange;
  granularity: Granularity;
  family: PeriodFamily;
  scope: ScopeFilters;
  appointmentTypes: string[];
  excludeZeroApptDays: boolean;
  /** True when sums must come from dvmWeeks (wide) rather than siteWeeks. */
  measuresFromDvmWeeks: boolean;
}

export function buildTrendFromRollup(input: RollupTrendInput): TrendBucketTotals[] {
  const {
    siteWeeks,
    dvmWeeks,
    edgeRows,
    range,
    granularity,
    family,
    scope,
    appointmentTypes,
    excludeZeroApptDays: nz,
    measuresFromDvmWeeks,
  } = input;

  const typeFilterActive = appointmentTypes.length > 0;
  const buckets = new Map<string, Acc>();
  const acc = (key: string): Acc => {
    let a = buckets.get(key);
    if (!a) {
      a = emptyAcc();
      buckets.set(key, a);
    }
    return a;
  };

  // Same rule useTrendData applies: a calendar period's part-week bucket would be
  // drawn the width of a full week, so it is dropped rather than plotted.
  const include = (key: string) => isBucketIncluded(key, granularity, family, range);

  // ── Sums, from whichever rollup carries the measures ──────────────────────
  const measureRows = measuresFromDvmWeeks ? dvmWeeks : siteWeeks;
  for (const r of measureRows) {
    if (!matchesScope(r, scope)) continue;
    const key = bucketOfWeek(String(r.week_start), granularity, family);
    if (!include(key)) continue;
    const a = acc(key);
    a.sched += num(nz ? r.scheduled_minutes_nonzero : r.scheduled_minutes);
    a.worked += num(nz ? r.total_worked_minutes_nonzero : r.total_worked_minutes);
    a.blockoff += num(nz ? r.blockoff_minutes_nonzero : r.blockoff_minutes);
    a.allSeen += num(
      nz ? r.completed_appointment_minutes_nonzero : r.completed_appointment_minutes
    );
    a.avail += num(nz ? r.available_minutes_nonzero : r.available_minutes);
    a.medRev += num(nz ? r.medical_net_sales_amount_nonzero : r.medical_net_sales_amount);
    a.completed += num(nz ? r.completed_appointments_nonzero : r.completed_appointments);
    if (typeFilterActive) {
      for (const slug of appointmentTypes) {
        a.selectedKind += num(r[`appointment_kind_${slug}_minutes`]);
      }
    }
  }

  // ── Distinct counts, from the masks ───────────────────────────────────────
  for (const r of dvmWeeks) {
    if (!matchesScope(r, scope)) continue;
    const m = nz ? r.day_mask_nonzero : r.day_mask;
    if (m === 0) continue;
    const key = bucketOfWeek(String(r.week_start), granularity, family);
    if (!include(key)) continue;
    const a = acc(key);
    const dw = `${r.resource_id}|${r.week_start}`;
    a.perDvmWeek.set(dw, (a.perDvmWeek.get(dw) ?? 0) | m);
    a.perWeek.set(String(r.week_start), (a.perWeek.get(String(r.week_start)) ?? 0) | m);
  }

  // ── The ragged edge ───────────────────────────────────────────────────────
  // Bucketed by the row's own DATE, which is what getBucketKey does for raw rows
  // and the only correct rule for a calendar month.
  const edgeKept = edgeRows.filter(
    (r) => matchesScope(r, scope) && (!nz || Number(r.completed_appointment_minutes) > 0)
  );
  for (const r of edgeKept) {
    const key = getBucketKey(r.schedule_date, granularity, family);
    if (!include(key)) continue;
    const a = acc(key);
    a.sched += Number(r.scheduled_minutes);
    a.worked += Number(r.total_worked_minutes);
    a.blockoff += Number(r.blockoff_minutes);
    a.allSeen += Number(r.completed_appointment_minutes);
    a.avail += Number(r.available_minutes);
    a.medRev += Number(r.medical_net_sales_amount) || 0;
    a.completed += Number(r.completed_appointments) || 0;
    a.edgeDvmDays.add(`${r.resource_id}|${r.schedule_date}`);
    a.edgeDates.add(r.schedule_date);
    // The ISO week, so this matches the whole-week side's notion of a DVM-week.
    a.edgeDvmWeeks.add(`${r.resource_id}|${getBucketKey(r.schedule_date, "week", family)}`);
  }
  // Selected-kind minutes for the edge come from the shared helper, so the slug
  // sum cannot drift from the one metrics.ts uses.
  if (typeFilterActive) {
    const byBucket = new Map<string, SchedulingRow[]>();
    for (const r of edgeKept) {
      const key = getBucketKey(r.schedule_date, granularity, family);
      if (!include(key)) continue;
      if (!byBucket.has(key)) byBucket.set(key, []);
      byBucket.get(key)!.push(r);
    }
    for (const [key, rows] of byBucket) {
      acc(key).selectedKind += sumSelectedKindMinutes(rows, appointmentTypes);
    }
  }

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucketKey, a]) => {
      let dvmDays = 0;
      for (const m of a.perDvmWeek.values()) dvmDays += popcount7(m);
      let distinctDates = 0;
      for (const m of a.perWeek.values()) distinctDates += popcount7(m);
      return {
        bucketKey,
        dvmDays: dvmDays + a.edgeDvmDays.size,
        dvmWeeks: a.perDvmWeek.size + a.edgeDvmWeeks.size,
        distinctDates: distinctDates + a.edgeDates.size,
        totalScheduledMin: a.sched,
        totalWorkedMin: a.worked,
        totalBlockoffMin: a.blockoff,
        allSeenMin: a.allSeen,
        totalAvailMin: a.avail,
        selectedKindMin: a.selectedKind,
        totalMedRev: a.medRev,
        totalCompleted: a.completed,
      };
    });
}
